import { useState } from 'react';

import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import { Alert, Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { testMaxConnection } from '../../api/maxSettingsApi';
import { useMaxSettings, useUpdateMaxSettings } from '../../hooks';
import { deliveryFieldError, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  MaxMessageFormat,
  MaxSettings,
  UpdateMaxSettingsRequest,
} from '../../model/settingsTypes';
import {
  ConfiguredSecretField,
  FeedbackSnackbar,
  FieldRow,
  MovedSettingsNotice,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';

function toUpdateRequest(
  draft: MaxSettings,
  botTokenCandidate: string,
  webhookSecretCandidate: string,
): UpdateMaxSettingsRequest {
  // Destructured only to omit them from `rest` — `_`-prefixed names satisfy `no-unused-vars`.
  const {
    botSecretConfigured: _botSecretConfigured,
    updatedAt: _updatedAt,
    updatedBy: _updatedBy,
    ...rest
  } = draft;
  const token = botTokenCandidate.trim();
  const secret = webhookSecretCandidate.trim();
  return {
    ...rest,
    botToken: token === '' ? null : token,
    webhookSecret: secret === '' ? null : secret,
  };
}

/**
 * MAX settings — connection + delivery (plan-conversation-001 WP-MP.2).
 *
 * Shrunk from the former 436-line page with a second `MaxServiceSettings` resource
 * (design-channel-connection-pages.md §3.2): behaviour toggles moved conceptually to Chatbot /
 * Sales channels / Bot texts & FAQ, and `/max/service` was merged into this one resource
 * server-side (WP-BFF.2) — `serviceSettingsVersion` is that merged half's own optimistic lock,
 * independent of `version` (bot identity). No interim chatbot-behaviour card here (unlike
 * Telegram) — the plan's sequencing fix names one bridge, hosted once, not one per channel page.
 */
export function MaxSettingsPage() {
  const { t } = useTranslation('settings');
  const MESSAGE_FORMAT_OPTIONS = [
    { value: 'markdown', label: t('maxSettings.formattingSection.messageFormatOptions.markdown') },
    { value: 'html', label: t('maxSettings.formattingSection.messageFormatOptions.html') },
  ];
  const { canView, canManage, canManageCredentials } = useDomainPermissions('max');
  const chatbotPermissions = useDomainPermissions('chatbot');
  const templatesPermissions = useDomainPermissions('chatbot-templates');
  const channelsPermissions = useDomainPermissions('channels');

  const query = useMaxSettings();
  const update = useUpdateMaxSettings();
  const form = useSettingsForm(query.data);

  // ── Delivery & limits validation (stage defect 27.09: a cleared/out-of-range field could reach
  // the adapter and 422 as INVALID_RANGE — see lib/deliveryFieldValidation.ts) ──────────────────
  const draftForValidation = form.draft;
  const maxRetriesError = draftForValidation
    ? deliveryFieldError(
        t,
        'maxSettings.deliverySection',
        draftForValidation.deliveryMaxRetries,
        0,
        10,
      )
    : null;
  const retryBackoffError = draftForValidation
    ? deliveryFieldError(
        t,
        'maxSettings.deliverySection',
        draftForValidation.deliveryRetryBackoffMs,
        0,
      )
    : null;
  const sendTimeoutError = draftForValidation
    ? deliveryFieldError(
        t,
        'maxSettings.deliverySection',
        draftForValidation.sendTimeoutMs,
        100,
        120000,
      )
    : null;
  const rateLimitError = draftForValidation
    ? deliveryFieldError(
        t,
        'maxSettings.deliverySection',
        draftForValidation.rateLimitPerChatPerMin,
        1,
      )
    : null;
  const deliveryInvalid = Boolean(
    maxRetriesError || retryBackoffError || sendTimeoutError || rateLimitError,
  );

  // Write-only credential candidates, kept out of `form.draft` so a 409 refetch never discards
  // what the operator typed — see TelegramSettingsPage's identical pattern for the full rationale.
  const [botTokenCandidate, setBotTokenCandidate] = useState('');
  const [webhookSecretCandidate, setWebhookSecretCandidate] = useState('');

  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty =
    form.dirty || botTokenCandidate.trim() !== '' || webhookSecretCandidate.trim() !== '';

  const reset = () => {
    form.reset();
    setBotTokenCandidate('');
    setWebhookSecretCandidate('');
  };

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toUpdateRequest(form.draft, botTokenCandidate, webhookSecretCandidate), {
      onSuccess: () => {
        setBotTokenCandidate('');
        setWebhookSecretCandidate('');
        success(t('maxSettings.savedFeedback'));
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('maxSettings.title')}
      subtitle={t('maxSettings.subtitle')}
      icon={<ChatRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      saveDisabled={deliveryInvalid}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        const credentialDisabled = disabled || !canManageCredentials;

        return (
          <Stack spacing={2.5}>
            <MovedSettingsNotice
              channel="max"
              canViewChatbot={chatbotPermissions.canView}
              canViewTemplates={templatesPermissions.canView}
              canViewChannels={channelsPermissions.canView}
            />

            {!canManage && canView ? (
              <Alert severity="info">{t('scaffold.readOnlyNotice')}</Alert>
            ) : null}
            {canManage && !canManageCredentials ? (
              <Alert severity="info">{t('maxSettings.credentialsRestrictedNotice')}</Alert>
            ) : null}

            <SettingsCard
              title={t('maxSettings.botConnectionSection.title')}
              icon={<SmartToyRoundedIcon />}
            >
              <TextInputField
                label={t('maxSettings.identitySection.botCodeLabel')}
                description={t('maxSettings.identitySection.botCodeDescription')}
                value={draft.botCode ?? ''}
                onChange={(value) => form.setField('botCode', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('maxSettings.identitySection.botUsernameLabel')}
                description={t('maxSettings.identitySection.botUsernameDescription')}
                value={draft.botUsername ?? ''}
                onChange={(value) => form.setField('botUsername', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('maxSettings.identitySection.botNameLabel')}
                description={t('maxSettings.identitySection.botNameDescription')}
                value={draft.botName ?? ''}
                onChange={(value) => form.setField('botName', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('maxSettings.identitySection.botEnabledLabel')}
                description={t('maxSettings.botConnectionSection.botEnabledDescription')}
                checked={draft.botEnabled}
                onChange={(value) => form.setField('botEnabled', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('maxSettings.identitySection.miniAppUrlLabel')}
                description={t('maxSettings.identitySection.miniAppUrlDescription')}
                value={draft.miniAppUrl ?? ''}
                onChange={(value) => form.setField('miniAppUrl', value)}
                disabled={disabled}
                type="url"
              />
            </SettingsCard>

            <SettingsCard
              title={t('maxSettings.secretsSection.title')}
              icon={<SecurityRoundedIcon />}
            >
              {draft.botEnabled && !draft.botSecretConfigured ? (
                <Alert
                  severity="warning"
                  icon={<KeyRoundedIcon fontSize="inherit" />}
                  sx={{ mb: 1 }}
                >
                  {t('maxSettings.secretsSection.noTokenWarning')}
                </Alert>
              ) : null}
              <ConfiguredSecretField
                label={t('maxSettings.secretsSection.botTokenLabel')}
                description={t('maxSettings.secretsSection.botTokenDescription')}
                configured={draft.botSecretConfigured}
                secret={botTokenCandidate}
                onSecretChange={setBotTokenCandidate}
                disabled={credentialDisabled}
              />
              <ConfiguredSecretField
                label={t('maxSettings.secretsSection.webhookSecretLabel')}
                description={t('maxSettings.secretsSection.webhookSecretDescription')}
                configured={undefined}
                secret={webhookSecretCandidate}
                onSecretChange={setWebhookSecretCandidate}
                disabled={credentialDisabled}
                optional
              />
              {/* Hidden (not merely disabled) without the credentials permission: this action
                  calls the live MAX API with the bot's real (candidate or stored) token. */}
              {canManageCredentials ? (
                <FieldRow
                  label={t('maxSettings.secretsSection.testConnectionLabel')}
                  description={t('maxSettings.secretsSection.testConnectionDescription')}
                  alignTop
                >
                  <TestConnectionButton
                    run={() =>
                      testMaxConnection(
                        botTokenCandidate.trim()
                          ? { botToken: botTokenCandidate.trim() }
                          : undefined,
                      )
                    }
                  />
                </FieldRow>
              ) : null}
            </SettingsCard>

            <SettingsCard
              title={t('maxSettings.deliveryFormattingSection.title')}
              icon={<SpeedRoundedIcon />}
              action={
                draft.serviceSettingsVersion != null ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('scaffold.versionChip', { version: draft.serviceSettingsVersion })}
                  />
                ) : undefined
              }
            >
              <SelectField
                label={t('maxSettings.formattingSection.messageFormatLabel')}
                description={t('maxSettings.formattingSection.messageFormatDescription')}
                value={draft.defaultFormat}
                options={MESSAGE_FORMAT_OPTIONS}
                onChange={(value) => form.setField('defaultFormat', value as MaxMessageFormat)}
                disabled={disabled}
              />
              <SwitchField
                label={t('maxSettings.formattingSection.disableLinkPreviewLabel')}
                description={t('maxSettings.formattingSection.disableLinkPreviewDescription')}
                checked={draft.disableLinkPreview}
                onChange={(value) => form.setField('disableLinkPreview', value)}
                disabled={disabled}
              />
              <NumberField
                label={t('maxSettings.deliverySection.maxRetriesLabel')}
                description={t('maxSettings.deliverySection.maxRetriesDescription')}
                value={draft.deliveryMaxRetries}
                onChange={(value) => form.setField('deliveryMaxRetries', value)}
                disabled={disabled}
                min={0}
                max={10}
                error={Boolean(maxRetriesError)}
                helperText={maxRetriesError}
              />
              <NumberField
                label={t('maxSettings.deliverySection.retryBackoffLabel')}
                description={t('maxSettings.deliverySection.retryBackoffDescription')}
                value={draft.deliveryRetryBackoffMs}
                onChange={(value) => form.setField('deliveryRetryBackoffMs', value)}
                disabled={disabled}
                min={0}
                suffix={t('maxSettings.deliverySection.retryBackoffSuffix')}
                error={Boolean(retryBackoffError)}
                helperText={retryBackoffError}
              />
              <NumberField
                label={t('maxSettings.deliverySection.sendTimeoutLabel')}
                description={t('maxSettings.deliverySection.sendTimeoutDescription')}
                value={draft.sendTimeoutMs}
                onChange={(value) => form.setField('sendTimeoutMs', value)}
                disabled={disabled}
                min={100}
                max={120000}
                suffix={t('maxSettings.deliverySection.sendTimeoutSuffix')}
                error={Boolean(sendTimeoutError)}
                helperText={sendTimeoutError}
              />
              <NumberField
                label={t('maxSettings.deliverySection.rateLimitLabel')}
                description={t('maxSettings.deliverySection.rateLimitDescription')}
                value={draft.rateLimitPerChatPerMin}
                onChange={(value) => form.setField('rateLimitPerChatPerMin', value)}
                disabled={disabled}
                min={1}
                suffix={t('maxSettings.deliverySection.rateLimitSuffix')}
                error={Boolean(rateLimitError)}
                helperText={rateLimitError}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
